// PafPdfContent.jsx
import React from "react";
import {
  Page,
  Text,
  View,
  Document,
  StyleSheet,
  Image,
} from "@react-pdf/renderer";

// -------------------------------------------
// STYLES
// -------------------------------------------
const styles = StyleSheet.create({
  page: {
    fontSize: 10,
    fontFamily: "Helvetica",
    paddingTop: 40,
    paddingBottom: 40,
    paddingHorizontal: 30,
  },

  header: {
    marginBottom: 20,
    borderBottomWidth: 2,
    borderBottomColor: "#334155",
    paddingBottom: 10,
  },

  title: {
    fontSize: 20,
    fontWeight: "bold",
    color: "#334155",
    marginBottom: 5,
  },

  subtitle: {
    fontSize: 12,
    color: "#64748b",
  },

  section: {
    marginTop: 15,
    marginBottom: 10,
  },

  sectionTitle: {
    fontSize: 14,
    fontWeight: "bold",
    color: "#334155",
    marginBottom: 8,
    backgroundColor: "#f1f5f9",
    padding: 6,
    borderLeftWidth: 3,
    borderLeftColor: "#334155",
  },

  row: {
    flexDirection: "row",
    marginBottom: 6,
  },

  label: {
    fontSize: 10,
    fontWeight: "bold",
    width: "35%",
    color: "#475569",
  },

  value: {
    fontSize: 10,
    width: "65%",
    color: "#1e293b",
  },

  grid: {
    flexDirection: "row",
    flexWrap: "wrap",
    marginBottom: 10,
  },

  gridItem: {
    width: "50%",
    marginBottom: 6,
  },

  gridItemFull: {
    width: "100%",
    marginBottom: 6,
  },

  table: {
    display: "table",
    width: "auto",
    borderStyle: "solid",
    borderWidth: 1,
    borderColor: "#cbd5e1",
    marginTop: 10,
    marginBottom: 10,
  },

  tableRow: {
    flexDirection: "row",
    borderBottomWidth: 1,
    borderBottomColor: "#cbd5e1",
  },

  tableHeader: {
    backgroundColor: "#e2e8f0",
    fontWeight: "bold",
  },

  tableCell: {
    padding: 6,
    fontSize: 9,
    borderRightWidth: 1,
    borderRightColor: "#cbd5e1",
  },

  tableCellHeader: {
    padding: 6,
    fontSize: 9,
    fontWeight: "bold",
    borderRightWidth: 1,
    borderRightColor: "#cbd5e1",
  },

  listItem: {
    fontSize: 9,
    marginBottom: 3,
    marginLeft: 10,
  },

  badge: {
    backgroundColor: "#dbeafe",
    color: "#1e40af",
    padding: 4,
    borderRadius: 3,
    fontSize: 8,
    marginRight: 5,
  },

  horizontalLine: {
    borderBottomWidth: 1,
    borderBottomColor: "#e2e8f0",
    marginVertical: 10,
  },

  footer: {
    position: "absolute",
    bottom: 30,
    left: 30,
    right: 30,
    textAlign: "center",
    fontSize: 8,
    color: "#94a3b8",
    borderTopWidth: 1,
    borderTopColor: "#e2e8f0",
    paddingTop: 10,
  },
});

// -------------------------------------------
// HELPER FUNCTIONS
// -------------------------------------------
const formatDate = (dateString) => {
  if (!dateString) return "N/A";
  const date = new Date(dateString);
  return date.toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
};

const formatDateTime = (dateString) => {
  if (!dateString) return "N/A";
  const date = new Date(dateString);
  return date.toLocaleString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

// -------------------------------------------
// COMPONENTS
// -------------------------------------------

const InfoRow = ({ label, value }) => (
  <View style={styles.row}>
    <Text style={styles.label}>{label}:</Text>
    <Text style={styles.value}>{value || "N/A"}</Text>
  </View>
);

const GridInfoItem = ({ label, value, fullWidth = false }) => (
  <View style={fullWidth ? styles.gridItemFull : styles.gridItem}>
    <View style={styles.row}>
      <Text style={styles.label}>{label}:</Text>
      <Text style={styles.value}>{value || "N/A"}</Text>
    </View>
  </View>
);

const SectionTitle = ({ title }) => (
  <Text style={styles.sectionTitle}>{title}</Text>
);

// -------------------------------------------
// MAIN PAF PDF COMPONENT
// -------------------------------------------
const PafPdfContent = ({ data }) => {
  const paf = data?.paf || {};
  const complaintSite = Array.isArray(data?.complaint_site) ? data.complaint_site : [];
  const onset = data?.onset || {};
  const radiationReferral = data?.radiation_referral || {};
  const characterData = Array.isArray(data?.character_data) ? data.character_data : [];
  const associatedComplaints = Array.isArray(data?.associated_complaints) ? data.associated_complaints : [];
  const healthConditions = Array.isArray(data?.health_conditions) ? data.health_conditions : [];
  const healthConditionsOther = data?.health_conditions_other || "";
  const pastReports = Array.isArray(data?.past_reports) ? data.past_reports : [];
  const pastReportsOther = data?.past_reports_other || "";
  const allergies = data?.allergies || {};
  const painFactors = Array.isArray(data?.pain_factors) ? data.pain_factors : [];
  const painFactorsOther = data?.pain_factors_other || "";
  const medicationHistory = data?.medication_history || {};
  const familyHistory = data?.family_history || {};
  const pastTreatments = data?.past_treatments || {};
  const primaryPain = data?.primary_pain || {};
  const doctorDetails = data?.doctor_details || {};
  const clinicDetails = data?.clinic_details || {};

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        {/* Header */}
        <View style={styles.header}>
          <Text style={styles.title}>Pain Assessment Form (PAF)</Text>
          <Text style={styles.subtitle}>
            {clinicDetails.name || "Clinic"} · {clinicDetails.city || ""}
          </Text>
          <Text style={styles.subtitle}>
            Doctor: {doctorDetails.full_name || "N/A"}
          </Text>
        </View>

        {/* Patient Information */}
        <View style={styles.section}>
          <SectionTitle title="Patient Information" />
          <View style={styles.grid}>
            <GridInfoItem label="Name" value={paf.name} />
            <GridInfoItem label="Age" value={paf.age ? `${paf.age} years` : ""} />
            <GridInfoItem label="Gender" value={paf.gender} />
            <GridInfoItem label="Mobile" value={paf.mobile_number} />
            <GridInfoItem label="City" value={paf.city} />
            <GridInfoItem label="Occupation" value={paf.occupation} />
            <GridInfoItem label="Date & Time" value={formatDateTime(paf.date_time)} fullWidth />
          </View>
        </View>

        {/* Insurance Information */}
        <View style={styles.section}>
          <SectionTitle title="Insurance Information" />
          <View style={styles.grid}>
            <GridInfoItem label="Category" value={paf.insurance_category} />
            <GridInfoItem label="Type" value={paf.insurance_type} />
          </View>
        </View>

        {/* Vitals */}
        <View style={styles.section}>
          <SectionTitle title="Vitals" />
          <View style={styles.grid}>
            <GridInfoItem label="BP" value={paf.bp} />
            <GridInfoItem label="Pulse" value={paf.pulse} />
            <GridInfoItem 
              label="Height" 
              value={paf.height_feet && paf.height_inch ? `${paf.height_feet}' ${paf.height_inch}"` : ""} 
            />
            <GridInfoItem label="Weight" value={paf.weight ? `${paf.weight} kg` : ""} />
            <GridInfoItem label="BMI" value={paf.bmi} />
          </View>
        </View>

        {/* Lifestyle */}
        <View style={styles.section}>
          <SectionTitle title="Lifestyle" />
          <InfoRow label="Activity Level" value={paf.activityLevel} />
          <InfoRow label="Habits" value={Array.isArray(paf.habits) && paf.habits.length > 0 ? paf.habits.join(", ") : ""} />
          <InfoRow label="Contact Window" value={Array.isArray(paf.contact_window) && paf.contact_window.length > 0 ? paf.contact_window.join(", ") : ""} />
        </View>

        {/* Complaint Notes */}
        <View style={styles.section}>
          <SectionTitle title="Complaint Notes" />
          <Text style={styles.value}>{paf.complaintsNotes ? paf.complaintsNotes.replace(/<[^>]*>/g, '') : "N/A"}</Text>
        </View>

        {/* Primary Pain Site */}
        <View style={styles.section}>
          <SectionTitle title="Primary Pain Site" />
          <InfoRow label="Site" value={primaryPain.pain_site} />
          <InfoRow 
            label="Location" 
            value={[
              primaryPain.left && "Left",
              primaryPain.right && "Right",
              primaryPain.center && "Center"
            ].filter(Boolean).join(", ") || "N/A"} 
          />
          <InfoRow label="Duration" value={primaryPain.duration} />
          {primaryPain.duration === "1 m" && primaryPain.pain_days && (
            <InfoRow label="Pain Days" value={primaryPain.pain_days} />
          )}
          <InfoRow label="Pain Score" value={primaryPain.pain_score !== undefined && primaryPain.pain_score !== null ? primaryPain.pain_score : ""} />
        </View>

        {/* Secondary Pain Sites */}
        <View style={styles.section}>
          <SectionTitle title="Secondary Pain Sites" />
          {complaintSite && complaintSite.filter(site => site.painScore).length > 0 ? (
            <View style={styles.table}>
              <View style={[styles.tableRow, styles.tableHeader]}>
                <Text style={[styles.tableCellHeader, { width: "30%" }]}>Site</Text>
                <Text style={[styles.tableCellHeader, { width: "25%" }]}>Location</Text>
                <Text style={[styles.tableCellHeader, { width: "25%" }]}>Duration</Text>
                <Text style={[styles.tableCellHeader, { width: "20%" }]}>Pain Score</Text>
              </View>
              {complaintSite
                .filter(site => site.painScore)
                .map((site, index) => (
                  <View key={index} style={styles.tableRow}>
                    <Text style={[styles.tableCell, { width: "30%" }]}>{site.name}</Text>
                    <Text style={[styles.tableCell, { width: "25%" }]}>
                      {[
                        site.left && "Left",
                        site.right && "Right",
                        site.center && "Center"
                      ].filter(Boolean).join(", ")}
                    </Text>
                    <Text style={[styles.tableCell, { width: "25%" }]}>{site.duration}</Text>
                    <Text style={[styles.tableCell, { width: "20%" }]}>{site.painScore}</Text>
                  </View>
                ))}
            </View>
          ) : (
            <Text style={styles.value}>No secondary pain sites recorded</Text>
          )}
        </View>

        {/* Onset */}
        <View style={styles.section}>
          <SectionTitle title="Onset" />
          <InfoRow label="Onset Type" value={onset.onsetType} />
          <InfoRow label="Progress Type" value={onset.progressType} />
          <InfoRow label="Progress Speed" value={onset.progressSpeed} />
        </View>

        {/* Radiation/Referral */}
        <View style={styles.section}>
          <SectionTitle title="Radiation/Referral" />
          <InfoRow 
            label="Radiation" 
            value={radiationReferral.radiation?.selected ? (radiationReferral.radiation.text || "Yes") : "No"} 
          />
          <InfoRow 
            label="Referral" 
            value={radiationReferral.referral?.selected ? (radiationReferral.referral.text || "Yes") : "No"} 
          />
        </View>

        {/* Character */}
        <View style={styles.section}>
          <SectionTitle title="Character of Pain" />
          <Text style={styles.value}>{characterData && characterData.length > 0 ? characterData.join(", ") : "N/A"}</Text>
        </View>

        {/* Associated Complaints */}
        <View style={styles.section}>
          <SectionTitle title="Associated Complaints" />
          <Text style={styles.value}>{associatedComplaints && associatedComplaints.length > 0 ? associatedComplaints.join(", ") : "N/A"}</Text>
        </View>

        {/* Health Conditions */}
        <View style={styles.section}>
          <SectionTitle title="Health Conditions" />
          <Text style={styles.value}>{healthConditions.length > 0 ? healthConditions.join(", ") : "N/A"}</Text>
          {healthConditionsOther && (
            <InfoRow label="Other" value={healthConditionsOther} />
          )}
        </View>

        {/* Past Reports */}
        <View style={styles.section}>
          <SectionTitle title="Past Reports" />
          <Text style={styles.value}>{pastReports.length > 0 ? pastReports.join(", ") : "N/A"}</Text>
          {pastReportsOther && (
            <InfoRow label="Other" value={pastReportsOther} />
          )}
        </View>

        {/* Pain Factors */}
        <View style={styles.section}>
          <SectionTitle title="Pain Triggers & Relievers" />
          {painFactors.filter(f => f.aggravating || f.relieving).length > 0 ? (
            <View style={styles.table}>
              <View style={[styles.tableRow, styles.tableHeader]}>
                <Text style={[styles.tableCellHeader, { width: "60%" }]}>Factor</Text>
                <Text style={[styles.tableCellHeader, { width: "20%" }]}>Aggravating</Text>
                <Text style={[styles.tableCellHeader, { width: "20%" }]}>Relieving</Text>
              </View>
              {painFactors
                .filter(f => f.aggravating || f.relieving)
                .map((factor, index) => (
                  <View key={index} style={styles.tableRow}>
                    <Text style={[styles.tableCell, { width: "60%" }]}>{factor.name}</Text>
                    <Text style={[styles.tableCell, { width: "20%" }]}>{factor.aggravating ? "+" : ""}</Text>
                    <Text style={[styles.tableCell, { width: "20%" }]}>{factor.relieving ? "-" : ""}</Text>
                  </View>
                ))}
            </View>
          ) : (
            <Text style={styles.value}>No pain factors recorded</Text>
          )}
          {painFactorsOther && (
            <InfoRow label="Other Factors" value={painFactorsOther} />
          )}
        </View>

        {/* Allergies */}
        <View style={styles.section}>
          <SectionTitle title="Allergies" />
          {allergies.none ? (
            <Text style={styles.value}>None</Text>
          ) : (
            <>
              <Text style={styles.value}>
                {Array.isArray(allergies.multi) && allergies.multi.length > 0 ? allergies.multi.join(", ") : "N/A"}
              </Text>
              {allergies.otherText && (
                <InfoRow label="Other" value={allergies.otherText} />
              )}
            </>
          )}
        </View>

        {/* Medication History */}
        <View style={styles.section}>
          <SectionTitle title="Medication History" />
          {medicationHistory.steroids || medicationHistory.anticoagulants || medicationHistory.othersText ? (
            <>
              {medicationHistory.steroids && <Text style={styles.listItem}>• Steroids</Text>}
              {medicationHistory.anticoagulants && <Text style={styles.listItem}>• Anticoagulants</Text>}
              {medicationHistory.othersText && (
                <InfoRow label="Others" value={medicationHistory.othersText} />
              )}
            </>
          ) : (
            <Text style={styles.value}>N/A</Text>
          )}
        </View>

        {/* Family History */}
        <View style={styles.section}>
          <SectionTitle title="Family History" />
          <InfoRow label="Has History" value={familyHistory.hasHistory} />
          <InfoRow label="Details" value={familyHistory.details} />
        </View>

        {/* Past Treatments */}
        <View style={styles.section}>
          <SectionTitle title="Past Treatments" />
          {pastTreatments.none ? (
            <Text style={styles.value}>None</Text>
          ) : (
            <>
              {pastTreatments.pms?.selected && (
                <InfoRow label="PMS" value={pastTreatments.pms.option} />
              )}
              {pastTreatments.surgeon?.selected && (
                <InfoRow label="Surgeon" value={pastTreatments.surgeon.option} />
              )}
              {pastTreatments.physio && <Text style={styles.listItem}>• Physio</Text>}
              {pastTreatments.otc && <Text style={styles.listItem}>• OTC</Text>}
              {pastTreatments.physician && <Text style={styles.listItem}>• Physician</Text>}
              {pastTreatments.altTreatments && <Text style={styles.listItem}>• Alternative Treatments</Text>}
              {!pastTreatments.pms?.selected && !pastTreatments.surgeon?.selected && 
               !pastTreatments.physio && !pastTreatments.otc && 
               !pastTreatments.physician && !pastTreatments.altTreatments && (
                <Text style={styles.value}>N/A</Text>
              )}
            </>
          )}
        </View>

        {/* Footer */}
        <View style={styles.footer}>
          <Text>Generated on {formatDateTime(new Date().toISOString())}</Text>
          <Text>Pain Assessment Form - Confidential Medical Document</Text>
        </View>
      </Page>
    </Document>
  );
};

export default PafPdfContent;
